import React, { useRef } from 'react';
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from 'framer-motion';

const wrap = (min: number, max: number, v: number) => {
  const range = max - min;
  return ((((v - min) % range) + range) % range) + min;
};

/** An endless ribbon of words that speeds up and changes direction with scrolling. */
export function Marquee({ items, speed = 2.2 }: { items: string[]; speed?: number }) {
  const reduce = useReducedMotion();
  const base = useMotionValue(0);
  const { scrollY } = useScroll();
  const velocity = useSpring(useVelocity(scrollY), { damping: 50, stiffness: 400 });
  const factor = useTransform(velocity, [-1000, 0, 1000], [-4, 0, 4], { clamp: false });
  const direction = useRef(1);
  const x = useTransform(base, (v) => `${wrap(-50, 0, v)}%`);

  useAnimationFrame((_, delta) => {
    if (reduce) return;
    let move = direction.current * speed * (delta / 1000);
    const f = factor.get();
    if (f < 0) direction.current = -1;
    else if (f > 0) direction.current = 1;
    move += direction.current * move * Math.abs(f);
    base.set(base.get() - Math.abs(move) * direction.current);
  });

  const row = (hidden: boolean) => (
    <div className="marquee__row" aria-hidden={hidden}>
      {items.map((item, i) => (
        <span key={item} className="marquee__item">
          <span className={i % 2 ? 'serif' : ''}>{item}</span>
          <svg className="marquee__star" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 0v24M0 12h24M3.5 3.5l17 17M20.5 3.5l-17 17" stroke="currentColor" strokeWidth="1.6" />
          </svg>
        </span>
      ))}
    </div>
  );

  return (
    <section className="marquee" aria-label="Fields of work">
      <motion.div className="marquee__track" style={{ x }}>
        {row(false)}
        {row(true)}
      </motion.div>
    </section>
  );
}
